// frontend/src/pages/Fees/FeesDashboard.js
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import feeAPI from '../../utils/feeAPI';
import { LoadingState, EmptyState } from '../../components/ui';

const fmt = n => `₹${(n||0).toLocaleString('en-IN')}`;

const STATUS_STYLE = {
  paid:    { bg:'#D1FAE5', color:'#065F46', label:'Paid' },
  partial: { bg:'#DBEAFE', color:'#1E40AF', label:'Partial' },
  pending: { bg:'#FEF3C7', color:'#92400E', label:'Pending' },
  overdue: { bg:'#FEE2E2', color:'#991B1B', label:'Overdue' },
  waived:  { bg:'#F3F4F6', color:'#374151', label:'Waived' },
};

export default function FeesDashboard({ onNavigate }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [txpModal, setTxpModal] = useState(false);
  const now = new Date();
  const [txpForm, setTxpForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear(), dueDate: '' });
  const [txpBusy, setTxpBusy] = useState(false);

  // Reusable loader so we can call it on mount, focus, interval, button click
  const reload = React.useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const [dRes, overdueRes, recentRes] = await Promise.all([
        feeAPI.getDashboard().catch(()=>({ data:{ data:{} } })),
        feeAPI.getAssignments({ status:'overdue' }).catch(()=>({ data:{ data:[] } })),
        feeAPI.getRecentPayments(8).catch(()=>({ data:{ data:[] } })),
      ]);
      const d = dRes.data.data || {};
      setData({
        ...d,
        overdueList:    (overdueRes.data.data||[]).slice(0,5),
        recentPayments: recentRes.data.data || [],
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => { reload(); }, [reload]);

  // Refetch when window regains focus (admin returns from another tab/CollectFees)
  useEffect(() => {
    const onFocus = () => reload({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  // Auto-refresh every 30 seconds in the background
  useEffect(() => {
    const id = setInterval(() => reload({ silent: true }), 30000);
    return () => clearInterval(id);
  }, [reload]);

  if (loading) return <LoadingState />;

  // Backend returns: totalExpected, totalCollected, totalPending, overdueCount, todayCollection
  const totalAssigned  = data?.totalExpected   || data?.totalAssigned  || 0;
  const totalCollected = data?.totalCollected  || 0;
  const totalPending   = data?.totalPending    || 0;
  const totalOverdue   = data?.overdueCount    || data?.totalOverdue   || 0;
  const todayCollection= data?.todayCollection || 0;
  const recentPayments = data?.recentPayments  || [];
  const overdueList    = data?.overdueList     || [];
  const collectionRate = totalAssigned > 0 ? Math.min(100, Math.round((totalCollected/totalAssigned)*100)) : totalCollected > 0 ? 100 : 0;

  const KPI = ({ label, value, sub, color, bg, onClick }) => (
    <div onClick={onClick} style={{ background:bg||'#fff', border:`1.5px solid ${color}30`, borderRadius:14, padding:'18px 20px', cursor:onClick?'pointer':'default', transition:'all 0.15s' }}
      className={onClick?"hover:-translate-y-1 hover:shadow-md":""}>
      <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:900, color }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 className="font-display text-2xl text-ink">💰 Fee Dashboard</h2>
          <p className="text-sm text-muted mt-0.5">Overview of school fee collection</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <button onClick={()=>setTxpModal(true)}
            style={{ padding:'9px 16px', borderRadius:10, background:'#0B1F4A', color:'#fff', border:'none', fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
            🚌 Generate Transport Fees
          </button>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:700, textTransform:'uppercase' }}>Today's Collection</div>
            <div style={{ fontSize:24, fontWeight:900, color:'#16A34A' }}>{fmt(todayCollection)}</div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        <KPI label="Total Assigned"  value={fmt(totalAssigned)}   color="#1D4ED8" bg="#EFF6FF" sub={`${data?.totalStudents||0} students`}    onClick={()=>onNavigate?.('report')}/>
        <KPI label="Collected"       value={fmt(totalCollected)}  color="#16A34A" bg="#F0FDF4" sub={`${collectionRate}% collection rate`}     onClick={()=>onNavigate?.('slip')}/>
        <KPI label="Pending"         value={fmt(totalPending)}    color="#D97706" bg="#FFFBEB" sub={`${data?.assignmentCount||0} assignments`}   onClick={()=>onNavigate?.('collect')}/>
        <KPI label="Overdue"         value={fmt(totalOverdue)}    color="#DC2626" bg="#FEF2F2" sub={`${data?.overdueCount||0} students`}      onClick={()=>onNavigate?.('report')}/>
      </div>

      {/* Collection Rate Bar */}
      <div className="card" style={{ padding:'16px 20px', marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <span style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Collection Rate</span>
          <span style={{ fontSize:15, fontWeight:900, color: collectionRate>=80?'#16A34A':collectionRate>=50?'#D97706':'#DC2626' }}>{collectionRate}%</span>
        </div>
        <div style={{ height:10, background:'#F3F4F6', borderRadius:5, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${collectionRate}%`, background: collectionRate>=80?'#16A34A':collectionRate>=50?'#D97706':'#DC2626', borderRadius:5, transition:'width 1s' }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#9CA3AF', marginTop:6 }}>
          <span>Collected: {fmt(totalCollected)}</span>
          <span>Remaining: {fmt(totalPending)}</span>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Recent Payments */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid #E5E7EB' }}>
            <span style={{ fontWeight:700, fontSize:14 }}>
              Recent Payments
              {refreshing && <span style={{ fontSize:10, color:'#9CA3AF', fontWeight:500, marginLeft:8 }}>refreshing…</span>}
            </span>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <button onClick={()=>reload({ silent:true })} disabled={refreshing}
                style={{ fontSize:12, color:'#16A34A', background:'#F0FDF4', border:'1px solid #BBF7D0', padding:'4px 10px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                ↻ Refresh
              </button>
              <button onClick={()=>onNavigate?.('slip')} style={{ fontSize:12, color:'#1D4ED8', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>View all →</button>
            </div>
          </div>
          {!recentPayments.length ? (
            <EmptyState icon="💳" title="No payments yet" subtitle="Payments will appear here"/>
          ) : recentPayments.map((p,i) => (
            <div key={i} onClick={()=>onNavigate?.('slip')}
              style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 18px', borderBottom:'0.5px solid #F3F4F6', cursor:'pointer', transition:'background 0.1s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#F8FAFF'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
              <div style={{ width:38, height:38, borderRadius:10, background:'#D1FAE5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>✅</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {p.studentName || p.student?.user?.name || '—'}
                </div>
                <div style={{ fontSize:11, color:'#9CA3AF' }}>
                  {p.className ? `${p.className} · ` : ''}{p.month || p.feeType || 'Payment'} · {p.paidOn ? new Date(p.paidOn).toLocaleDateString('en-IN') : '—'}
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontWeight:800, fontSize:14, color:'#16A34A' }}>{fmt(p.amount)}</div>
                {p.receiptNumber && <div style={{ fontSize:10, color:'#9CA3AF' }}>#{p.receiptNumber}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* Overdue / Pending */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid #E5E7EB' }}>
            <span style={{ fontWeight:700, fontSize:14 }}>⚠️ Overdue / Pending</span>
            <button onClick={()=>onNavigate?.('report')} style={{ fontSize:12, color:'#1D4ED8', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Manage →</button>
          </div>
          {!overdueList.length ? (
            <EmptyState icon="✅" title="No overdue fees!" subtitle="All students are up to date"/>
          ) : overdueList.map((a,i) => {
            const ss = STATUS_STYLE[a.status] || STATUS_STYLE.pending;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 18px', borderBottom:'0.5px solid #F3F4F6' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {a.student?.user?.name || '—'}
                  </div>
                  <div style={{ fontSize:11, color:'#9CA3AF' }}>{a.feeType?.name} · Due {a.dueDate ? new Date(a.dueDate).toLocaleDateString('en-IN') : '—'}</div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontWeight:800, fontSize:13, color:'#DC2626' }}>{fmt(a.pendingAmount || a.finalAmount)}</div>
                  <span style={{ fontSize:10, fontWeight:700, color:ss.color, background:ss.bg, padding:'2px 8px', borderRadius:20 }}>{ss.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Generate Transport Fees modal ──────────────────────────────── */}
      {txpModal && (
        <div onClick={()=>!txpBusy && setTxpModal(false)} style={{ position:'fixed', inset:0, zIndex:400, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:16 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:460, marginTop:60, overflow:'hidden' }}>
            <div style={{ background:'#0B1F4A', padding:'15px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ color:'#fff', fontWeight:800, fontSize:15 }}>🚌 Generate Transport Fees</span>
              <button onClick={()=>!txpBusy && setTxpModal(false)} style={{ background:'rgba(255,255,255,0.15)', color:'#fff', border:'none', borderRadius:8, width:30, height:30, cursor:'pointer', fontSize:16 }}>×</button>
            </div>
            <div style={{ padding:20 }}>
              <p style={{ fontSize:13, color:'#6B7280', marginTop:0, marginBottom:16 }}>
                Creates a transport fee line for every student with <b>active transport</b>, using each student's own monthly fee. Students already billed for this month are skipped.
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'#6B7280', display:'block', marginBottom:4 }}>MONTH</label>
                  <select value={txpForm.month} onChange={e=>setTxpForm(f=>({...f, month:Number(e.target.value)}))} style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13 }}>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=>(
                      <option key={m} value={i+1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'#6B7280', display:'block', marginBottom:4 }}>YEAR</label>
                  <input type="number" value={txpForm.year} onChange={e=>setTxpForm(f=>({...f, year:Number(e.target.value)}))} style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom:18 }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#6B7280', display:'block', marginBottom:4 }}>DUE DATE (optional)</label>
                <input type="date" value={txpForm.dueDate} onChange={e=>setTxpForm(f=>({...f, dueDate:e.target.value}))} style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                <button onClick={()=>setTxpModal(false)} disabled={txpBusy} style={{ padding:'10px 20px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', color:'#374151', fontSize:13, fontWeight:700, cursor:'pointer' }}>Cancel</button>
                <button disabled={txpBusy} onClick={async ()=>{
                  setTxpBusy(true);
                  try {
                    const res = await feeAPI.generateTransportFees(txpForm);
                    toast.success(res.data?.message || 'Transport fees generated');
                    setTxpModal(false);
                    reload({ silent:true });
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'Failed to generate transport fees');
                  } finally { setTxpBusy(false); }
                }} style={{ padding:'10px 24px', borderRadius:10, background:'#0B1F4A', color:'#fff', border:'none', fontSize:13, fontWeight:700, cursor:'pointer', opacity:txpBusy?0.7:1 }}>
                  {txpBusy ? '⏳ Generating…' : '🚌 Generate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}